/**
 * Navbar component for the website.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {Object} props.data - Data used for rendering the Navbar.
 * @param {boolean} props.showCart - A boolean to determine if the cart should be displayed.
 * @param {function} props.setIsUberBannerPresent - Function to set the presence of Uber Banner.
 * @returns {JSX.Element} Returns the Navbar component.
 */

import React, { useCallback, useContext, useEffect, useState } from "react";
import MaticLogo from "../../../assets/svg/MaticLogo";
import MaticLogoBlack from "../../../assets/svg/MaticLogoBlack";
import UberBanner from "../UberBanner";
import MobileMenu from "./MobileMenu";
import styles from "./NavBar.module.scss";
import Link from "next/link";
import { UberBannerContext } from "@/providers/UberBannerProvider";
import { useRouter } from "next/navigation";
import useCart from "@/cart/useCart";
import { MATIC_VARIANT_ID } from "../../consts/shopify";
import RewardApplied from "@/components/RewardApplied/RewardApplied";
import RewardAppliedMobile from "@/components/RewardApplied/RewardAppliedMobile";
import { useMediaQuery } from "@/hooks/useMediaQuery";

const Navbar = ({ data, showCart, setIsUberBannerPresent }) => {
  const [showNav, setShowNav] = useState(false);

  const scrollHandler = useCallback(e => {
    const targetEl = e?.target;

    const scrollingEl = targetEl?.scrollingElement;

    const scrollingElTop = scrollingEl?.scrollTop || 0;

    setShowNav(scrollingElTop > 1);
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", scrollHandler, true);

    return () => {
      window.removeEventListener("scroll", scrollHandler, true);
    };
  }, [scrollHandler]);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const router = useRouter();
  const { cart, addItem } = useCart();
  const cartQuantity = cart?.totalQuantity || 0;
  const isMobile = useMediaQuery("(max-width: 1200px)");
  const isReferralApplied = Boolean(
    cart?.discountCodes?.some(dc => dc?.code?.toUpperCase?.().startsWith("FRIEND-"))
  );
  const mobileBannerVisible = Boolean(isMobile && isReferralApplied);
  const mobileOverlayTop = mobileBannerVisible ? 95 : 55;

  const goToCartCheckout = async () => {
    if (cartQuantity === 0) {
      try {
        await addItem(MATIC_VARIANT_ID, 1);
        router.push("/cart");
      } catch (error) {
        console.error("Error adding Matic to cart:", error);
      }
    } else {
      router.push("/cart");
    }
  };

  // const [showBanner, setShowBannerAnimate] = useState(true);

  // useEffect(() => {
  //   const isShowBannerKeyPresent = localStorage.getItem("showUberBanner");
  //   setShowBannerAnimate(isShowBannerKeyPresent !== 'false');
  // }, [showBanner]);

  return (
    <nav className={`sticky top-0 z-[2]`}>
      <div
        className={`absolute w-full top-[0px] ${
          true ? "" : `${styles.slideUp}`
        }`}
      >
        <RewardAppliedMobile />
        {/* <UberBanner
          setShowBannerAnimate={setShowBannerAnimate}
          bannerTextData={data}
          setIsUberBannerPresent={setIsUberBannerPresent}
        /> */}
        <ul
          className={`flex justify-between w-full border-t border-black items-center ${
            styles.maticLogoSvg
          } ${styles.sweepToBottom} h-[55px] ${
            !showNav
              ? "border-b border-white20 bg-[transparent]"
              : `border-b border-primary ${styles.sweepToBottom} ${styles.sweepToBottomActive}`
          }`}
        >
          <li><MobileMenu data={data} overlayTop={mobileOverlayTop} /></li>
          <li><Link
            href="/product"
            className={`px-6 flex items-center h-full min-w-[87px] lg:min-w-[143.4px] mob:hidden ${
              !showNav
                ? "text-white"
                : "text-primary hover:bg-primary hover:text-white cursor-pointer"
            }`}
          >
            <p className="hidden lg:block">Explore Matic</p>
            <p className="block lg:hidden">Explore</p>
          </Link></li>
          <li><Link
            href="/accessories"
            className={`px-6 border-l flex items-center h-full min-w-[87px] lg:min-w-[143.4px] mob:hidden ${
              !showNav
                ? "text-white border-white20"
                : "text-primary hover:bg-primary hover:text-white cursor-pointer"
            }`}
          >
            {/* TODO: Replace with dynamic data */}
            <p className="hidden lg:block">Shop Accessories</p>
            <p className="block lg:hidden">Accessories</p>
          </Link></li>
          <li
            className={`flex flex-1 items-center justify-center flex-shrink-0 border-l h-full cursor-pointer mob:pr-0 pr-[165px] lg:pr-[220px]  ${
              !showNav ? "border-white20" : ""
            } ${showCart ? "mr-[9%]" : ""}`}
            onClick={scrollToTop}
          >
            {!showNav ? (
              <MaticLogo classes={`${styles.maticLogoSvg}`} />
            ) : (
              <MaticLogoBlack classes={`${styles.maticLogoSvg}`} />
            )}
          </li>
          <li><RewardApplied /></li>
          {!showCart && (
            <li
              className={`flex items-center h-full gap-4 px-6 min-w-[80px] lg:min-w-[134px] border-l ${showNav ? "border-primary" : "border-white20"}`}
            >
              <button
                className="flex items-center h-full gap-4"
                onClick={goToCartCheckout}
              >
                {cartQuantity === 0 ? (
                  <p className={`${!showNav ? "text-white" : "text-primary"}`}>
                    Order Now
                  </p>
                ) : (
                  <>
                    <p
                      className={`hidden lg:block ${!showNav ? "text-white" : "text-primary"}`}
                    >
                      {data.navbarCartText}{" "}
                    </p>
                    <span
                      className={`eyebrow-regular bg-primary text-white rounded-[6px] lg:rounded-8 w-3 h-3 flex items-center justify-center p-4 font-medium`}
                    >
                      {cartQuantity}
                    </span>
                  </>
                )}
              </button>
            </li>
          )}
        </ul>
      </div>
    </nav>
  );
};

export default Navbar;
